import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position } from '@xyflow/react';
import { Box, Maximize2, Minimize2, Loader2, X } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { useUpstreamFile } from '../useUpstreamFile';
import { sheetsService, SchemaColumn } from '../../../services/sheetsService';
import { s3Config } from '../../../aws-config';
import Scene3D from '../../visualize/Scene3D';
import ControlPanel from '../../visualize/ControlPanel';
import ViewControls from '../../visualize/ViewControls';
import CameraControls from '../../visualize/CameraControls';
import {
  DataRow,
  LayoutType,
  PaletteType,
  ThemeType,
  ShapeType,
  SortDirection,
  SizeDimension,
  ViewSettings,
  Position3D,
} from '../../../types/visualization';
import {
  THEMES,
  DEFAULT_VIEW_SETTINGS,
  DEFAULT_VISUALIZATION_SETTINGS,
  rowMatchesSearch,
} from '../../../constants/visualizationThemes';

// ---------------------------------------------------------------------------
// Data processing helper (shared between mini and fullscreen)
// ---------------------------------------------------------------------------
interface ProcessedVizData {
  rows: DataRow[];
  columns: string[];
  numericColumns: string[];
  columnCardinalities: Record<string, number>;
  defaultXAxis: string;
  defaultColorColumn: string;
  defaultScatterX: string;
  defaultScatterY: string;
}

function processVizData(rawRows: Record<string, any>[]): ProcessedVizData | null {
  if (!rawRows || rawRows.length === 0) return null;

  const rows: DataRow[] = rawRows.map((row, idx) => ({ ...row, _id: idx }));
  const columns = Object.keys(rawRows[0]).filter(k => k !== '_id');

  const columnCardinalities: Record<string, number> = {};
  columns.forEach(col => {
    columnCardinalities[col] = new Set(rows.map(r => r[col])).size;
  });

  const numericColumns = columns.filter(col => {
    const samples = rows.slice(0, 100).map(r => r[col]).filter(v => v !== '' && v != null);
    if (samples.length === 0) return false;
    const numCount = samples.filter(v => { const n = parseFloat(String(v)); return !isNaN(n) && Number.isFinite(n); }).length;
    return numCount / samples.length > 0.8;
  });

  const sorted = [...columns].sort((a, b) => (columnCardinalities[a] || 0) - (columnCardinalities[b] || 0));
  const defaultXAxis = sorted.find(c => columnCardinalities[c] > 1 && columnCardinalities[c] < 50) || columns[0];

  return {
    rows,
    columns,
    numericColumns,
    columnCardinalities,
    defaultXAxis,
    defaultColorColumn: defaultXAxis,
    defaultScatterX: numericColumns[0] || columns[0],
    defaultScatterY: numericColumns[1] || numericColumns[0] || columns[0],
  };
}

// ---------------------------------------------------------------------------
// Fullscreen Visualization Modal
// ---------------------------------------------------------------------------
interface FullscreenVizProps {
  vizData: ProcessedVizData;
  onClose: () => void;
}

function FullscreenViz({ vizData, onClose }: FullscreenVizProps) {
  const { rows, columns, numericColumns, columnCardinalities } = vizData;

  // Visualization state
  const [layout, setLayout] = useState<LayoutType>(DEFAULT_VISUALIZATION_SETTINGS.layout);
  const [xAxis, setXAxis] = useState(vizData.defaultXAxis);
  const [sortColumn, setSortColumn] = useState('originalIndex');
  const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_VISUALIZATION_SETTINGS.sortDirection);
  const [colorColumn, setColorColumn] = useState(vizData.defaultColorColumn);
  const [colorPalette, setColorPalette] = useState<PaletteType>(DEFAULT_VISUALIZATION_SETTINGS.colorPalette);
  const [searchQuery, setSearchQuery] = useState('');
  const [theme, setTheme] = useState<ThemeType>(DEFAULT_VISUALIZATION_SETTINGS.theme);
  const [scatterXColumn, setScatterXColumn] = useState(vizData.defaultScatterX);
  const [scatterYColumn, setScatterYColumn] = useState(vizData.defaultScatterY);
  const [searchHighlightColor] = useState(DEFAULT_VISUALIZATION_SETTINGS.searchHighlightColor);
  const [cubeMagnification, setCubeMagnification] = useState(DEFAULT_VISUALIZATION_SETTINGS.cubeMagnification);
  const [metallicMode, setMetallicMode] = useState(DEFAULT_VISUALIZATION_SETTINGS.metallicMode);
  const [shapeType, setShapeType] = useState<ShapeType>(DEFAULT_VISUALIZATION_SETTINGS.shapeType);
  const [sizeByColumn, setSizeByColumn] = useState<string | null>(null);
  const [sizeByDimension, setSizeByDimension] = useState<SizeDimension>(DEFAULT_VISUALIZATION_SETTINGS.sizeByDimension);
  const [maxSizeMultiplier, setMaxSizeMultiplier] = useState(DEFAULT_VISUALIZATION_SETTINGS.maxSizeMultiplier);
  const [viewSettings, setViewSettings] = useState<ViewSettings>({ ...DEFAULT_VIEW_SETTINGS, autoRotate: true });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const controlsRef = useRef<any>(null);

  const searchMatchCount = useMemo(() => {
    if (!searchQuery) return 0;
    return rows.filter(row => rowMatchesSearch(row, searchQuery)).length;
  }, [rows, searchQuery]);

  const handleViewChange = useCallback((key: keyof ViewSettings, value: boolean | number) => {
    setViewSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleResetCamera = useCallback(() => {
    controlsRef.current?.reset();
  }, []);

  // ESC to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const t = THEMES[theme];

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: t.background,
      }}
    >
      {/* 3D Scene */}
      <Scene3D
        allData={rows}
        layout={layout}
        xAxis={xAxis}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        colorColumn={colorColumn}
        colorPalette={colorPalette}
        searchQuery={searchQuery}
        pinnedSearches={[]}
        setSelectedData={() => {}}
        onShiftClick={() => {}}
        hoveredIndex={hoveredIndex}
        setHoveredIndex={setHoveredIndex}
        viewSettings={viewSettings}
        controlsRef={controlsRef}
        theme={theme}
        scatterXColumn={scatterXColumn}
        scatterYColumn={scatterYColumn}
        searchHighlightColor={searchHighlightColor}
        cubeMagnification={cubeMagnification}
        metallicMode={metallicMode}
        shapeType={shapeType}
        sizeByColumn={sizeByColumn}
        sizeByDimension={sizeByDimension}
        maxSizeMultiplier={maxSizeMultiplier}
      />

      {/* Header */}
      <div style={{
        position: 'absolute', top: 20, left: 20, zIndex: 100,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: theme === 'dark' ? '#fff' : '#1d1d1f' }}>
          BigSplice Cube Engine
        </h1>
        <p style={{ margin: '6px 0 0', color: t.textMuted, fontSize: 12 }}>
          {rows.length.toLocaleString()} rows &bull; Grouped by {xAxis || 'none'}
        </p>
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 20, right: 20, zIndex: 100,
          width: 40, height: 40, borderRadius: '50%',
          background: t.panelBg, border: `1px solid ${t.panelBorder}`,
          color: t.text, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(20px)',
        }}
        title="Close (Esc)"
      >
        <Minimize2 size={18} />
      </button>

      {/* Control panels */}
      <ViewControls
        settings={viewSettings}
        onChange={handleViewChange}
        onResetCamera={handleResetCamera}
        theme={theme}
        onThemeChange={setTheme}
        metallicMode={metallicMode}
        onMetallicChange={setMetallicMode}
        shapeType={shapeType}
        onShapeChange={setShapeType}
      />

      <CameraControls controlsRef={controlsRef} theme={theme} />

      <ControlPanel
        layout={layout}
        xAxis={xAxis}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        colorColumn={colorColumn}
        colorPalette={colorPalette}
        searchQuery={searchQuery}
        onLayoutChange={setLayout}
        onXAxisChange={setXAxis}
        onSortChange={(col, dir) => { setSortColumn(col); setSortDirection(dir); }}
        onColorChange={setColorColumn}
        onPaletteChange={setColorPalette}
        onSearchChange={setSearchQuery}
        searchMatchCount={searchMatchCount}
        columns={columns}
        columnCardinalities={columnCardinalities}
        theme={theme}
        scatterXColumn={scatterXColumn}
        scatterYColumn={scatterYColumn}
        onScatterXChange={setScatterXColumn}
        onScatterYChange={setScatterYColumn}
        sizeByColumn={sizeByColumn}
        onSizeByColumnChange={setSizeByColumn}
        sizeByDimension={sizeByDimension}
        onSizeByDimensionChange={setSizeByDimension}
        maxSizeMultiplier={maxSizeMultiplier}
        onMaxSizeChange={setMaxSizeMultiplier}
        shapeType={shapeType}
        numericColumns={numericColumns}
      />

      {/* Bottom search bar + magnification */}
      <div style={{
        position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', alignItems: 'center', gap: 8, zIndex: 100,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          background: t.panelBg, border: `1px solid ${t.panelBorder}`,
          borderRadius: 6, padding: '0 12px', backdropFilter: 'blur(20px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search data..."
            style={{
              width: 180, padding: '8px 0', background: 'transparent',
              border: 'none', color: t.text, fontSize: 11, outline: 'none',
            }}
          />
          {searchQuery && (
            <>
              <span style={{ fontSize: 10, color: t.textMuted, marginRight: 4 }}>
                {searchMatchCount.toLocaleString()}
              </span>
              <button onClick={() => setSearchQuery('')} style={{
                background: 'none', border: 'none', color: t.textMuted,
                cursor: 'pointer', padding: 2, fontSize: 12, lineHeight: 1,
              }}>&times;</button>
            </>
          )}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: t.panelBg, border: `1px solid ${t.panelBorder}`,
          borderRadius: 6, padding: '6px 10px', backdropFilter: 'blur(20px)',
        }}>
          <input
            type="range" min="1" max="4" step="0.1"
            value={cubeMagnification}
            onChange={e => setCubeMagnification(parseFloat(e.target.value))}
            style={{ width: 60, height: 3, cursor: 'pointer', accentColor: t.textMuted }}
          />
          <span style={{ color: t.textMuted, fontSize: 10, minWidth: 24 }}>
            {cubeMagnification.toFixed(1)}x
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// VisualizeNode — main node component
// ---------------------------------------------------------------------------
export const VisualizeNode: React.FC<{ id: string; data: { label: string; color: string; isTemplate?: boolean } }> = ({ id, data }) => {
  const selectedFile = useUpstreamFile(id);
  const [loading, setLoading] = useState(false);
  const [vizData, setVizData] = useState<ProcessedVizData | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  // Mini-scene refs and state
  const miniControlsRef = useRef<any>(null);
  const miniViewSettings: ViewSettings = {
    ...DEFAULT_VIEW_SETTINGS,
    autoRotate: true,
    rotationSpeed: 1,
    showStars: false,
    showFog: true,
    showGrid: true,
  };

  // Load data from upstream file
  const loadData = useCallback(async () => {
    if (!selectedFile) return;
    setLoading(true);
    setVizData(null);

    try {
      const bucket = s3Config.primaryDataBucket;
      const s3Key = selectedFile.s3Key || `users/placeholder/${selectedFile.fileName}`;

      // Get schema first to find a text column for search
      const schemaRes = await sheetsService.getSchema(bucket, s3Key, { isMultipart: selectedFile.isMultipart });
      if (!schemaRes.success || !schemaRes.schema) return;

      const textCol = schemaRes.schema.find((c: SchemaColumn) =>
        c.data_type === 'BYTE_ARRAY' || c.data_type === 'Utf8' || c.data_type === 'LargeUtf8' || c.data_type.includes('Utf8')
      ) || schemaRes.schema[0];

      if (!textCol) return;

      // Fetch up to 500 rows
      const dataRes = await sheetsService.search(
        bucket, s3Key,
        { column: textCol.name, operator: 'contains', value: '' },
        { limit: 500, offset: 0, isMultipart: selectedFile.isMultipart },
      );

      if (!dataRes.success || !dataRes.data || dataRes.data.length === 0) return;

      const processed = processVizData(dataRes.data);
      if (processed) setVizData(processed);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (selectedFile) loadData();
    else setVizData(null);
  }, [selectedFile, loadData]);

  return (
    <>
      <div className="bg-card border rounded-xl shadow-md w-[320px] overflow-hidden">
        {/* Header */}
        <div
          className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
          style={{ background: data.color }}
        >
          <Box className="h-3.5 w-3.5" />
          {data.label}
          {selectedFile?.totalRows && (
            <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 bg-white/20 text-white">
              {selectedFile.totalRows.toLocaleString()} rows
            </Badge>
          )}
        </div>

        <div className="p-3 space-y-2">
          {/* No upstream file */}
          {!selectedFile && (
            <p className="text-[11px] text-muted-foreground text-center py-2">
              Connect a data source
            </p>
          )}

          {/* File name badge */}
          {selectedFile && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1">
              <span className="truncate">{selectedFile.fileName}</span>
            </div>
          )}

          {/* Loading spinner */}
          {selectedFile && loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Mini 3D Scene */}
          {selectedFile && !loading && vizData && (
            <div
              className="nodrag nowheel rounded-lg overflow-hidden relative"
              style={{ height: 180, background: '#0a0a0a' }}
            >
              <Scene3D
                allData={vizData.rows}
                layout={DEFAULT_VISUALIZATION_SETTINGS.layout}
                xAxis={vizData.defaultXAxis}
                sortColumn="originalIndex"
                sortDirection="desc"
                colorColumn={vizData.defaultColorColumn}
                colorPalette={DEFAULT_VISUALIZATION_SETTINGS.colorPalette}
                searchQuery=""
                pinnedSearches={[]}
                setSelectedData={() => {}}
                onShiftClick={() => {}}
                hoveredIndex={null}
                setHoveredIndex={() => {}}
                viewSettings={miniViewSettings}
                controlsRef={miniControlsRef}
                theme="dark"
                scatterXColumn={vizData.defaultScatterX}
                scatterYColumn={vizData.defaultScatterY}
                searchHighlightColor="#ff0000"
                cubeMagnification={1.8}
                metallicMode={true}
                shapeType="cube"
                sizeByColumn={null}
                sizeByDimension="y"
                maxSizeMultiplier={6}
              />

              {/* Fullscreen button overlay */}
              <button
                className="absolute top-2 right-2 z-10 bg-black/60 hover:bg-black/80 text-white rounded-md p-1.5 transition-colors backdrop-blur-sm"
                onClick={() => setFullscreen(true)}
                title="Fullscreen"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>

              {/* Row count overlay */}
              <div className="absolute bottom-1.5 left-2 z-10 text-[9px] text-white/50">
                {vizData.rows.length} rows &bull; {vizData.columns.length} cols
              </div>
            </div>
          )}

          {/* No data state */}
          {selectedFile && !loading && !vizData && (
            <div className="rounded-lg bg-muted/30 p-3 flex items-center justify-center h-[80px]">
              <p className="text-[10px] text-muted-foreground">No data available</p>
            </div>
          )}
        </div>

        <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
        <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
      </div>

      {/* Fullscreen modal */}
      {fullscreen && vizData && (
        <FullscreenViz vizData={vizData} onClose={() => setFullscreen(false)} />
      )}
    </>
  );
};
